import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Loader2, Clock, Plus, Save, GitCommit, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function WorkItemDetail() {
  const params = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const workItemId = params.id;
  
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [addingTime, setAddingTime] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newTimeEntry, setNewTimeEntry] = useState({ hours: 1, description: '', commit_id: '', commit_message: '' });

  useEffect(() => {
    loadWorkItem();
  }, [workItemId]);

  async function loadWorkItem() {
    try {
      const response = await axios.get(API_URL + '/api/work-items/' + workItemId, { headers: auth.getAuthHeader() });
      const data = response.data;
      setItem(data);
      setEditForm({ title: data.title, description: data.description || '', state: data.state, estimated_hours: data.estimated_hours || 0, assigned_to: data.assigned_to || '' });
    } catch (error) {
      toast.error('Tâche non trouvée');
      navigate('/work-items');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await axios.put(API_URL + '/api/work-items/' + workItemId, editForm, { headers: auth.getAuthHeader() });
      toast.success('Tâche mise à jour');
      loadWorkItem();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddTime(e) {
    e.preventDefault();
    setAddingTime(true);
    try {
      await axios.post(API_URL + '/api/time-entries', { work_item_id: workItemId, ...newTimeEntry }, { headers: auth.getAuthHeader() });
      toast.success('Temps ajouté');
      setTimeDialogOpen(false);
      setNewTimeEntry({ hours: 1, description: '', commit_id: '', commit_message: '' });
      loadWorkItem();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setAddingTime(false);
    }
  }

  async function handleDeleteTime(entryId) {
    try {
      await axios.delete(API_URL + '/api/time-entries/' + entryId, { headers: auth.getAuthHeader() });
      toast.success('Entrée supprimée');
      loadWorkItem();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  function getTypeClass(type) {
    if (type === 'Epic') return 'work-item-epic';
    if (type === 'Feature') return 'work-item-feature';
    if (type === 'UserStory') return 'work-item-userstory';
    if (type === 'Task') return 'work-item-task';
    if (type === 'Bug') return 'work-item-bug';
    return '';
  }

  function getStateClass(state) {
    if (state === 'Done' || state === 'Closed') return 'state-done';
    if (state === 'Active' || state === 'In Progress') return 'state-active';
    return 'state-new';
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!item) return null;

  const progress = item.estimated_hours > 0 ? Math.min((item.total_logged_hours / item.estimated_hours) * 100, 100) : 0;
  const timeEntries = item.time_entries || [];
  const children = item.children || [];

  return (
    <div className="space-y-6" data-testid="work-item-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={getTypeClass(item.work_item_type)}>{item.work_item_type}</Badge>
            <span className="font-mono text-sm text-muted-foreground">{item.id.substring(0, 8)}</span>
          </div>
          <h1 className="text-2xl font-heading font-extrabold mt-1">{item.title}</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Enregistrer</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="border-b border-border bg-muted/20 p-4"><CardTitle className="text-lg font-heading">Détails</CardTitle></CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre</Label>
                <Input id="title" value={editForm.title || ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={6} data-testid="edit-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>État</Label>
                  <Select value={editForm.state || 'New'} onValueChange={(v) => setEditForm({ ...editForm, state: v })}>
                    <SelectTrigger data-testid="edit-state-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Done">Done</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assigned">Assigné à</Label>
                  <Input id="assigned" value={editForm.assigned_to || ''} onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })} placeholder="email@exemple.com" data-testid="edit-assigned-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-border bg-muted/20 p-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">Entrées de temps</CardTitle>
              <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
                <DialogTrigger asChild><Button size="sm" className="rounded-sm" data-testid="add-time-btn"><Plus className="h-4 w-4 mr-1" />Ajouter</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Ajouter du temps</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddTime}>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="hours">Heures *</Label>
                        <Input id="hours" type="number" min="0.25" step="0.25" value={newTimeEntry.hours} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, hours: parseFloat(e.target.value) || 0 })} required data-testid="time-hours-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="timeDesc">Description</Label>
                        <Textarea id="timeDesc" value={newTimeEntry.description} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, description: e.target.value })} data-testid="time-description-input" />
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        <Label htmlFor="commitId">ID Commit</Label>
                        <Input id="commitId" value={newTimeEntry.commit_id} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, commit_id: e.target.value })} className="font-mono" data-testid="time-commit-id-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="commitMsg">Message Commit</Label>
                        <Input id="commitMsg" value={newTimeEntry.commit_message} onChange={(e) => setNewTimeEntry({ ...newTimeEntry, commit_message: e.target.value })} data-testid="time-commit-message-input" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setTimeDialogOpen(false)}>Annuler</Button>
                      <Button type="submit" disabled={addingTime} data-testid="submit-time-btn">{addingTime ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ajouter'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {timeEntries.length > 0 ? (
                <div className="divide-y divide-border">
                  {timeEntries.map(function(entry) {
                    return (
                      <div key={entry.id} className="flex items-center gap-4 p-4 hover:bg-muted/30" data-testid={'time-entry-' + entry.id}>
                        <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /><span className="font-semibold">{entry.hours}h</span></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{entry.description || 'Pas de description'}</p>
                          {entry.commit_id && <div className="flex items-center gap-1 mt-1"><GitCommit className="h-3 w-3 text-muted-foreground" /><span className="commit-hash">{entry.commit_id.substring(0, 7)}</span></div>}
                        </div>
                        <span className="text-xs text-muted-foreground">{new Date(entry.date).toLocaleDateString('fr-FR')}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteTime(entry.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">Aucune entrée de temps</div>
              )}
            </CardContent>
          </Card>

          {children.length > 0 && (
            <Card>
              <CardHeader className="border-b border-border bg-muted/20 p-4"><CardTitle className="text-lg font-heading">Éléments enfants</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {children.map(function(child) {
                    return (
                      <Link key={child.id} to={'/work-items/' + child.id} className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={getTypeClass(child.work_item_type)}>{child.work_item_type}</Badge>
                          <span className="font-medium">{child.title}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="border-b border-border bg-muted/20 p-4"><CardTitle className="text-lg font-heading">Suivi du temps</CardTitle></CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="estHours">Heures estimées</Label>
                <Input id="estHours" type="number" min="0" step="0.5" value={editForm.estimated_hours || 0} onChange={(e) => setEditForm({ ...editForm, estimated_hours: parseFloat(e.target.value) || 0 })} data-testid="edit-estimated-hours-input" />
              </div>
              <Separator />
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Complété</span><span className="font-semibold">{item.total_logged_hours || 0}h</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Restant</span><span className="font-semibold">{item.remaining_hours || 0}h</span></div>
                <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: progress + '%' }} /></div>
                <p className="text-xs text-muted-foreground text-center">{progress.toFixed(0)}% complété</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-border bg-muted/20 p-4"><CardTitle className="text-lg font-heading">Métadonnées</CardTitle></CardHeader>
            <CardContent className="p-6 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">ID</span><span className="font-mono">{item.id.substring(0, 8)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">État</span><Badge variant="secondary" className={getStateClass(item.state)}>{item.state}</Badge></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Créé</span><span>{new Date(item.created_at).toLocaleDateString('fr-FR')}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Modifié</span><span>{new Date(item.updated_at).toLocaleDateString('fr-FR')}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default WorkItemDetail;
